import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import {
  ignoreModalConfirm,
  ignoreModalError,
  ModalService,
  NotificationService,
  OkModalComponent,
  YesNoModalComponent,
} from 'tms-lib';

import { TmsWebBaseService } from '../base';
import { ApiResult, PickListChange, PickListChangeAnswer, PickListChangeCodes } from '../../models';

/**
 * The questions asked when a make purchase order's requirements or lines are changed while its
 * materials are on a Manufacturing Materials picking list (PRD
 * po-requirement-change-after-pick-list-printed). One place, so every screen that changes a make
 * PO words them the same way:
 *  - printed and being picked: refused, naming the list and who to speak to (D-1);
 *  - not printed yet: "Save anyway?" (D-2);
 *  - Picked/Complete: "Save anyway?", then - when the change adds material - "Add the new material
 *    to a new picking list?" (D-10).
 * Nothing is emailed (D-9) - the messages name the list's owner so the user can tell them.
 *
 * The API refuses the change with a coded 400 (see PickListChange) until it is re-posted with
 * `pickListChangeConfirmed: true`; a printed list refuses it whatever the flag says.
 */
@Injectable({
  providedIn: 'root',
})
export class PickListChangeService extends TmsWebBaseService {
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);

  constructor() {
    super('PurchaseOrderPickListChange');
  }

  /**
   * Pre-flight for a line quantity change. The screens ask this BEFORE their own "the order has
   * allocations" check, which would otherwise always answer first: fabric on a manufacturing list is
   * allocated fabric.
   */
  checkLineQuantity(purchaseOrderLineId: string, quantity: number): Observable<PickListChange> {
    return this.httpService.get<PickListChange>(
      `${this.apiUrl}/CheckLineQuantity?purchaseOrderLineId=${purchaseOrderLineId}&quantity=${quantity}`,
    );
  }

  /**
   * Runs a change; when the API refuses it for a picking list, asks, and on Yes runs it again with
   * `confirmed` true and then {@link afterSave}. Emits the result of the call that went through.
   * When refused outright or answered No it calls `declined` (put the screen back) and completes
   * without emitting. Any other error is passed on to the subscriber.
   */
  withConfirmation<T>(
    change: (confirmed: boolean) => Observable<T>,
    header: string,
    declined?: () => void,
  ): Observable<T> {
    return change(false).pipe(
      catchError((error: HttpErrorResponse) => {
        const refusal = this.refusal(error);
        if (!refusal) {
          return throwError(() => error);
        }

        return from(this.ask(refusal, header)).pipe(
          switchMap((answer: PickListChangeAnswer | undefined) => {
            if (!answer) {
              declined?.();
              return EMPTY;
            }
            return change(true).pipe(tap(() => this.afterSave(answer)));
          }),
        );
      }),
    );
  }

  /**
   * The same questions for a line QUANTITY change, asked before the change is posted (see
   * {@link checkLineQuantity}). Emits the answers when it may go ahead - post it with
   * `pickListChangeConfirmed: true` and call {@link afterSave} - or calls `declined` and completes.
   */
  confirmLineQuantity(
    purchaseOrderLineId: string,
    quantity: number,
    header: string,
    declined?: () => void,
  ): Observable<PickListChangeAnswer> {
    return this.checkLineQuantity(purchaseOrderLineId, quantity).pipe(
      switchMap((change: PickListChange) => from(this.ask(change, header))),
      switchMap((answer: PickListChangeAnswer | undefined) => {
        if (!answer) {
          declined?.();
          return EMPTY;
        }
        return of(answer);
      }),
    );
  }

  /** The refusal carried by a failed save or delete, or undefined when it is some other error. */
  refusal(error: HttpErrorResponse): PickListChange | undefined {
    const body = error?.error;
    return Object.values(PickListChangeCodes).some((code) => code === body?.code)
      ? (body as PickListChange)
      : undefined;
  }

  /**
   * Asks whatever the state calls for. Resolves with the answers when the change may go ahead (at
   * once, with nothing asked, when no list holds the materials), or undefined when it was refused or
   * the user said No - put the screen back as it was.
   */
  async ask(change: PickListChange, header: string): Promise<PickListChangeAnswer | undefined> {
    const goAhead: PickListChangeAnswer = {
      addToNewPickList: false,
      purchaseOrderHeaderIds: change?.purchaseOrderHeaderIds ?? [],
    };
    const lists = this.escape((change?.pickListNumbers ?? []).join(', '));
    const owner = this.escape(change?.owner) || $localize`the list's owner`;

    switch (change?.code) {
      case PickListChangeCodes.pickListPrinted: {
        const { modal, component } = this.modalService.openMedium<OkModalComponent>(OkModalComponent);
        component.header.set(header);
        component.message.set(
          $localize`The materials for this order are on picking list ${lists}:lists:, which ${owner}:owner: printed and is being picked. Its requirements and lines cannot be changed. Speak to ${owner}:owner:.`,
        );
        await modal.result.then(ignoreModalConfirm, ignoreModalError);
        return undefined;
      }

      case PickListChangeCodes.pickListNotPrinted:
        return (await this.yesNo(
          header,
          $localize`The materials for this order are on picking list ${lists}:lists:, which ${owner}:owner: created and has not printed yet. The list will not pick up this change, so let ${owner}:owner: know. Save anyway?`,
        ))
          ? goAhead
          : undefined;

      case PickListChangeCodes.pickListComplete: {
        const saveAnyway = await this.yesNo(
          header,
          $localize`The materials for this order were on picking list ${lists}:lists:, which ${owner}:owner: printed and is already complete. This change will not be picked from it, so let ${owner}:owner: know. Save anyway?`,
        );
        if (!saveAnyway) {
          return undefined;
        }

        // Only a change that adds material has anything to put on a list (D-10).
        return {
          ...goAhead,
          addToNewPickList:
            change.addsMaterial && (await this.yesNo(header, $localize`Add the new material to a new picking list?`)),
        };
      }

      default:
        return goAhead;
    }
  }

  /**
   * Run once the change has saved. On a Yes to "Add the new material to a new picking list?" raises
   * the list (D-7) and says what happened, including any material that could not go on it.
   */
  afterSave(answer: PickListChangeAnswer | undefined) {
    if (!answer?.addToNewPickList) {
      return;
    }

    this.httpService
      .post<ApiResult>(`${this.apiUrl}/RaiseNewPickList`, { purchaseOrderHeaderIds: answer.purchaseOrderHeaderIds })
      .subscribe({
        next: (result: ApiResult) => {
          if (result?.warnings?.length) {
            this.notificationService.success(result.warnings.join('\n'));
          }
          if (result?.cautions?.length) {
            this.notificationService.warning(result.cautions.join('\n'));
          }
        },
        error: (error: HttpErrorResponse) =>
          this.notificationService.apiFail(error, $localize`The new picking list could not be created.`),
      });
  }

  private async yesNo(header: string, message: string): Promise<boolean> {
    const { modal, component } = this.modalService.openMedium<YesNoModalComponent>(YesNoModalComponent);
    component.header.set(header);
    component.message.set(message);
    return modal.result.then(
      (yes: boolean) => !!yes,
      () => false,
    );
  }

  // The modals render their message as HTML; list numbers and a user's name are data.
  private escape(text: string | undefined): string {
    return (text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
